import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import useError from 'pages/Batch/utils/useError';
import usePremiumToaster from 'hooks/usePremiumToaster';
import useMemoSelector from 'hooks/useMemoSelector';
import { BATCH_ACTIONS, BATCH_TOOLS, TRANSPARENT_OPTION } from 'pages/Batch/configs';
import {
  getBatchActiveColor,
  getBatchActiveTexture,
  getCropAttrs,
  getEnhanceIntensity,
  getEnhanceMapping,
  getCropRotate,
  getCropFlip,
  getIsToolApplying,
} from 'store/batch/selectors';
import GridItemComponent from 'pages/Batch/components/GridItemComponent';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import errorLogger from 'services/errorLogger';
import { getUserData } from 'store/selectors/general';
import batchSlice from 'store/batch';
import { DataActionTypes } from 'pages/Batch/sections/Context';
import { BatchDataStatus } from 'configs/constants';
import analytics from 'pages/Batch/utils/analytics';
import { ActionTypes } from 'pages/Batch/sections/Context/types';
import { TBackgroundProp } from 'pages/Batch/types';
import loadingImage from 'utils/images/loadingImage';

const useToolCancelApply = () => {
  const dispatch = useDispatch();
  const {
    dispatchData,
    discardPending,
    setUploadedData,
    process,
    resizeParams,
    resetResizeParams,
    activeTool,
    setSelectedKeys,
    setActiveTool,
    selectedData,
    areImagesResizing,
    isWeb,
  } = useBatchData();

  const { activeTexture, cropAttrs, activeColor, enhanceIntensity, enhanceMapping, cropRotate, cropFlip, isToolApplying } = useMemoSelector(state => ({
    enhanceIntensity: getEnhanceIntensity(state),
    activeTexture: getBatchActiveTexture(state),
    enhanceMapping: getEnhanceMapping(state),
    activeColor: getBatchActiveColor(state),
    cropAttrs: getCropAttrs(state),
    cropRotate: getCropRotate(state),
    cropFlip: getCropFlip(state),
    isToolApplying: getIsToolApplying(state),
  }));

  const showToaster = usePremiumToaster();
  const { isSubscribed, waitingUserSubscription } = useMemoSelector(getUserData);

  useError();

  const [isApplyTriggered, setIsApplyTriggered] = useState(false);

  const selectedPhotoIds = useMemo(() => selectedData.map(x => x.key), [selectedData]);

  const isDone = useMemo(() => {
    const isActiveToolUsesPending = !!activeTool && [BATCH_TOOLS.crop, BATCH_TOOLS.resize].includes(activeTool);
    const isImagesProcessed = selectedData.every(({ status }) => status === BatchDataStatus.DONE || status === BatchDataStatus.ERROR);
    return isImagesProcessed || isActiveToolUsesPending;
  }, [selectedData, activeTool]);

  const onClose = useCallback(() => {
    setSelectedKeys([]);
    setActiveTool(null);
    resetResizeParams();
    setIsApplyTriggered(false);
  }, [setActiveTool, setSelectedKeys, resetResizeParams]);

  const handleDone = useCallback(async () => {
    if (!isWeb) {
      setIsApplyTriggered(true);

      if (areImagesResizing) {
        // Show loader for images
        dispatchData({
          type: ActionTypes.UPDATE_STATUSES,
          status: BatchDataStatus.IN_PROGRESS,
          skipItemsWithError: false,
          hideStatusIndicator: false,
        });
      }
    }

    if (!isDone || areImagesResizing) return;

    dispatch(batchSlice.actions.setIsToolApplying(true));
    dispatch(batchSlice.actions.setLatestAppliedTool(activeTool));

    const isPremiumTool = activeTool ? GridItemComponent[activeTool]?.isPremium : false;
    if (activeTool && isPremiumTool) {
      dispatch(batchSlice.actions.setAppliedPremiumTool(activeTool));
    }
    errorLogger.leaveBreadcrumb('sidebar_action', { type: 'apply', tool: activeTool });

    if (activeTool === BATCH_TOOLS.crop) {
      await process(BATCH_ACTIONS.applyCrop, { attrs: cropAttrs, rotate: cropRotate, flip: cropFlip }, true);
    }
    if (activeTool === BATCH_TOOLS.enhance && enhanceIntensity < 1) {
      await process(BATCH_ACTIONS.applyEnhance, { enhanceMapping, intensity: enhanceIntensity }, true);
    }
    if (activeTool === BATCH_TOOLS.removeBG && (activeTexture || (activeColor && activeColor !== TRANSPARENT_OPTION))) {
      const applyBackgroundProps: TBackgroundProp = {};
      if (activeTexture) {
        try {
          const texture = await loadingImage(activeTexture.url);
          applyBackgroundProps.texture = texture;
        } catch (err) {
          errorLogger.trigger(err as Error, 'error');
        }
      } else {
        applyBackgroundProps.color = activeColor as string;
      }

      await process(BATCH_ACTIONS.applyBackground, applyBackgroundProps, true);
    }
    if (activeTool === BATCH_TOOLS.watermark) {
      await process(BATCH_ACTIONS.watermark, {}, true);
    }

    if (activeTool && GridItemComponent[activeTool]?.processOnApply) {
      await process(BATCH_ACTIONS.resize, {
        ...resizeParams,
        bgColor: activeColor as string,
      }, true);
    }

    if (activeTool) {
      analytics.applyTool({
        isPremium: Boolean(isPremiumTool),
        parent: activeTool,
        name: activeTool,
        value: true,
      });
    }

    isPremiumTool && !isSubscribed && !waitingUserSubscription && showToaster();
    dispatchData({ type: DataActionTypes.APPLY_PENDING, selectedPhotoIds });
    dispatch(batchSlice.actions.setIsToolApplying(false));

    if (activeTool) {
      analytics.toolExit(activeTool, 'apply', selectedPhotoIds);
    }
    onClose();
  }, [
    waitingUserSubscription,
    isDone,
    activeTool,
    activeTexture,
    activeColor,
    isSubscribed,
    showToaster,
    dispatchData,
    onClose,
    process,
    cropAttrs,
    resizeParams,
    enhanceMapping,
    enhanceIntensity,
    dispatch,
    selectedPhotoIds,
    cropRotate,
    cropFlip,
    areImagesResizing,
    isWeb,
  ]);

  const handleCancel = useCallback(() => {
    discardPending();
    dispatch(batchSlice.actions.setIsToolApplying(false));
    if (activeTool) {
      analytics.toolExit(activeTool, 'cancel', selectedPhotoIds);
    }
    onClose();
    errorLogger.leaveBreadcrumb('sidebar_action', { type: 'cancel', tool: activeTool });
  }, [activeTool, discardPending, onClose, selectedPhotoIds, dispatch]);

  useEffect(() => {
    setUploadedData(prev => ({
      ...prev,
      count: 0,
    }));
  }, [setUploadedData]);

  useEffect(() => {
    if (isApplyTriggered && !areImagesResizing) {
      handleDone();
      setIsApplyTriggered(false);
    }
  }, [areImagesResizing, isApplyTriggered, handleDone]);

  return {
    handleCancel,
    handleDone,
    isDone,
    isToolApplying,
    isApplyTriggered,
  };
};

export default useToolCancelApply;
